import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { UpsellingBanner as F1ProductBanner } from '@factorialco/factorial-one-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18n } from 'modules/core/testing/i18n'

import { useCreateUpsellRequestWithErrorHandling } from 'modules/upselling_kit/hooks/useCreateUpsellRequest'
import { useGetUpsellingData } from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitProductBanner } from 'modules/upselling_kit/types'
import { useDefaultMessages } from 'modules/upselling_kit/utils/defaultMessages'
import {
  handleUpsellingLearnMore,
  handleUpsellingOnClose,
  handleUpsellingVisibility,
} from 'modules/upselling_kit/utils/handlers'

const ProductBannerContent = ({
  title,
  subtitle,
  mediaUrl,
  onClose,
  learnMoreNavigate,
  ...upsellProps
}: UpsellingKitProductBanner) => {
  const { create } = useCreateUpsellRequestWithErrorHandling()
  const { moduleRequested, requestSource, requestReason, featureRequested } = upsellProps

  const { moduleSlug } = useGetUpsellingData({
    moduleRequested,
  })
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const defaultMessages = useDefaultMessages()

  const handleUpsellCreateRequest = async () => {
    track(trackingEvents.upsellingKit.clickProductUpsellComponent, {
      source: requestSource,
      module: moduleRequested,
      component: 'product_banner',
    })
    await create({
      moduleRequested: moduleRequested,
      requestSource: requestSource,
      requestReason: requestReason,
      featureRequested: featureRequested,
    })
  }

  const handleLearnMore = () => {
    handleUpsellingLearnMore({
      moduleRequested,
      requestSource,
      component: 'product_banner',
    })
    if (learnMoreNavigate) {
      navigate(learnMoreNavigate)
    } else {
      navigate(
        navigation.discover.marketplaceModules.item.fullPath({
          id: moduleSlug || '',
        })
      )
    }
  }

  const handleOnClose = () => {
    handleUpsellingOnClose({
      onClose,
      requestSource,
      moduleRequested,
      component: 'product_banner',
    })
  }

  const buildPrimaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.request_information'),
      onClick: handleUpsellCreateRequest,
      variant: 'promote' as const,
      errorMessage: defaultMessages.error,
      successMessage: defaultMessages.success,
      nextSteps: defaultMessages.nextSteps,
      loadingState: defaultMessages.loading,
      closeLabel: defaultMessages.close,
      showConfirmation: true,
    }
  }

  const buildSecondaryAction = () => {
    return {
      label: i18n.t('upselling_kit.button.learn_more'),
      onClick: handleLearnMore,
      variant: 'default' as const,
    }
  }

  useEffect(() => {
    handleUpsellingVisibility({
      moduleRequested,
      requestSource,
      component: 'product_banner',
    })
  }, [moduleRequested, requestSource])

  return (
    <F1ProductBanner
      title={title}
      subtitle={subtitle}
      mediaUrl={mediaUrl}
      primaryAction={buildPrimaryAction()}
      secondaryAction={buildSecondaryAction()}
      onClose={handleOnClose}
    />
  )
}

export default ProductBannerContent
